import React, { Children, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import ResizeObserver from 'resize-observer-polyfill'

export type ScrollManagerProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>

const ScrollManagerChild = styled.div`
  min-height: calc(100vh - 55px);
  position: absolute;
  left: 0;
  right: 0;
  pointer-events: none;
  > * {
    pointer-events: initial;
  }
`

const ScrollManager = styled<React.FC<ScrollManagerProps>>(({ children, ...props }) => {
  const wrap = useRef<HTMLDivElement>(null)
  const childElements = useRef<HTMLDivElement[]>([])
  const [height, setHeight] = useState(0)
  const [heights, setHeights] = useState('')

  const observer = useMemo(
    () =>
      new ResizeObserver(() => {
        const heights = childElements.current.map((x) => x.getBoundingClientRect().height)
        setHeight(Math.max(...heights))
        setHeights(heights.join(''))
      }),
    []
  )

  useLayoutEffect(() => {
    const nextChildElements = Array.from(wrap.current?.children ?? []) as HTMLDivElement[]
    if (
      !childElements.current ||
      childElements.current.length !== nextChildElements.length ||
      childElements.current.some((x, i) => x !== nextChildElements[i])
    ) {
      childElements.current = nextChildElements
      observer.disconnect()
      nextChildElements.forEach((x) => observer.observe(x))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [observer, Children])

  const update = useCallback(() => {
    if (!height || !wrap.current || !heights) {
      return
    }
    const wrapPos = wrap.current.getBoundingClientRect()
    const headerHeight = 55
    if (wrapPos.top >= headerHeight) {
      for (const child of Array.from(wrap.current.children) as HTMLElement[]) {
        child.style.position = 'absolute'
        child.style.bottom = 'auto'
        child.style.top = '0px'
      }
    } else if (wrapPos.bottom - window.innerHeight <= 0) {
      for (const child of Array.from(wrap.current.children) as HTMLElement[]) {
        child.style.position = 'absolute'
        child.style.bottom = '0px'
        child.style.top = 'auto'
      }
    } else {
      const top = -wrapPos.top + headerHeight
      for (const child of Array.from(wrap.current.children) as HTMLElement[]) {
        const maxTop = Math.max(0, wrapPos.height - child.getBoundingClientRect().height)
        child.style.position = 'absolute'
        child.style.bottom = 'auto'
        child.style.top = Math.min(maxTop, top) + 'px'
      }
    }
  }, [height, heights])

  useEffect(() => {
    window.addEventListener('scroll', update, { passive: true })
    return () => {
      window.removeEventListener('scroll', update)
    }
  }, [update])

  useEffect(() => {
    update()
  }, [update])

  return (
    <div {...props} ref={wrap} style={{ height: height + 'px' }}>
      {React.Children.map(children, (child) => (
        <ScrollManagerChild>{child}</ScrollManagerChild>
      ))}
    </div>
  )
})`
  position: relative;
  min-height: calc(100vh - 55px);
  background-color: #000;
  position: relative;
  user-select: none;
  width: 100%;
  overflow: hidden;
`

export default ScrollManager
